<script setup lang="ts">
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

const { getPreferences } = usePreferencesCookie();
const { trackCtaViewMoreClicked } = useEvents();

const propertiesSearchUrl = computed(() => {
	const preferences = getPreferences();
	return PropertyFiltersPresenter.getPathFromFilters({
		cities: [...(preferences?.cities ?? ['São Paulo'])],
		propertyTypes: [...(preferences?.propertyTypes ?? [])],
		regions: [...(preferences?.regions ?? [])],
		maxAskingPrice: preferences?.askingPrice,
		states: ['SP']
	});
});

const handleStartCollection = () => {
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Começar coleção',
		[MixpanelProps.CURRENT_FLOW]: 'my collection',
		[MixpanelProps.SECTION_NAME]: 'onboarding collection page',
		[MixpanelProps.DESTINATION_URL]: propertiesSearchUrl.value
	});
};
</script>

<template>
	<OnboardingBase
		container-classes="flex min-h-full flex-col items-start justify-between gap-6 bg-white pb-6 md:justify-center md:py-14 2xl:py-16"
		content-area-classes="flex w-full flex-1 flex-col items-center justify-start gap-6 md:flex-none md:justify-center md:px-12"
		button-area-classes="flex w-full items-end justify-center md:items-start"
	>
		<!-- Image Slot -->
		<template #image>
			<div
				class="flex size-full max-h-[282px] max-w-full items-center justify-center overflow-hidden rounded-[1.667px] shadow-[0px_2px_6px_2px_rgba(0,0,0,0.15)] md:h-[300px] md:w-[400px] lg:h-[375px] lg:w-[500px]"
			>
				<NuxtImg
					src="/images/wishlist/onboarding.webp"
					format="webp"
					quality="80"
					preload
					fetchpriority="high"
					decoding="async"
					alt="Casal compartilhando imóveis em dispositivos móveis"
					class="size-full object-cover object-center"
					loading="eager"
				/>
			</div>
		</template>

		<!-- Content Slot -->
		<template #content>
			<div
				class="flex max-w-full flex-col items-center justify-start gap-3 px-5 text-center md:px-12 2xl:gap-4"
			>
				<div>
					<h1
						class="font-matter-sq text-[24px] leading-[150%] tracking-[-0.48px] text-black 2xl:text-[36px] 2xl:leading-normal 2xl:tracking-[-0.72px]"
					>
						Toda decisão é melhor quando compartilhada
					</h1>
				</div>
				<div class="flex flex-col items-center justify-start gap-2 2xl:gap-4">
					<p class="text-base font-normal leading-[150%] text-primary">
						Agora, seus imóveis favoritos estão sempre com você e com quem te
						acompanha nessa decisão.
					</p>

					<p
						class="text-center text-sm font-normal leading-[150%] text-primary-light-2"
					>
						Salve imóveis na sua coleção e compartilhe com família e amigos.
					</p>
				</div>
			</div>
		</template>

		<!-- Button Slot -->
		<template #button>
			<NuxtLink :to="propertiesSearchUrl" class="w-full px-5 md:w-auto md:px-0">
				<Button
					variant="primary"
					class="w-full gap-4 px-6 md:w-auto"
					@click="handleStartCollection"
				>
					<span
						class="whitespace-pre text-[18px] font-medium leading-normal text-white"
					>
						Começar coleção
					</span>
					<div>
						<IconMaterialSymbolsNorthEast class="size-6 text-beige" />
					</div>
				</Button>
			</NuxtLink>
		</template>
	</OnboardingBase>
</template>
